import { Button, IconButton, Menu, MenuList, Popover } from "@material-ui/core";
import classNames from "classnames";
import { getStaticUrl } from "js/config";
import { app } from "js/namespaces";
import { Gap5 } from "js/react/components/Gap";
import { themeColors } from "js/react/sharedStyles";
import { $ } from "js/vendor";
import React, { Component, Fragment } from "reactn";
import styled, { css } from "styled-components";
import { Icon } from "../../Components/Icon";

export const UIContainerContext = React.createContext(true);

export class UIContainer extends Component {
    state = {
        shouldRender: true,
        isOpening: false
    };

    constructor() {
        super();
        this.containerRef = React.createRef();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.isOpen && !this.props.isOpen) {
            this.props.onClose();
        }
    }

    render() {
        if (this.state.shouldRender) {
            return (
                <UIContainerContext.Provider value={this.state.isOpening}>
                    <div
                        ref={this.containerRef}
                        tabIndex="-1" // Needed to give default focus so 'event.relatedTarget' has something to work with
                        className={`beautiful-ui ui-container ${this.props.direction} ${this.props.className}`}
                    >
                        {this.props.children}
                    </div>
                </UIContainerContext.Provider>
            );
        } else {
            return null;
        }
    }
}

/**
 * @typedef {object} UIPaneProps
 * @property {React.Ref<HTMLDivElement>} [containerRef] - Ref to the pane's root div element.
 * @property {string} [className] - Additional CSS class name(s) for the pane container.
 * @property {React.ReactNode} [children] - The content to be rendered inside the pane.
 * @property {React.CSSProperties} [style] - Custom CSS styles for the pane container.
 */

/**
 * A basic container component used within a UI pane structure.
 * @augments {React.Component<UIPaneProps>}
 */
export class UIPane extends Component {
    render() {
        const { containerRef, className, children, ...props } = this.props;

        return (
            <div {...props} ref={containerRef} className={`ui-pane ${className ?? ""}`}>
                {children}
            </div>
        );
    }
}

export class UIPaneWithSideBar extends Component {
    render() {
        const { className, children, ...props } = this.props;

        return (
            <UIPane {...props} className={`with-sidebar ${className ?? ""}`}>
                {children}
            </UIPane>
        );
    }
}

export class UIPaneHeader extends Component {
    render() {
        const { className, onClose, children } = this.props;
        return (
            <div className={`ui-pane-header ${className ?? ""}`}>
                <Fragment>{children}</Fragment>
                {
                    onClose && (<IconButton className="exit-button" onClick={onClose}>
                        <Icon>close</Icon>
                    </IconButton>)
                }
            </div>
        );
    }
}

const Resizer = styled.div`
  position: absolute;
  height: 100%;
  top: 0px;
  width: 5px;
  right: 0px;
  cursor: col-resize;
`;

export const WarningBox = styled.div`
  color: white;
  font-size: 12px;
  font-weight: 600;
  color: #333333;
  text-transform: initial;
`;

export const StyledWarningIcon = styled(Icon)`
    color: #FF4500;
    font-size: 18px;
`;

export const StyledPanel = styled.div`
    &&& {
        display: flex;
        flex-direction: column;
        ${props => app.isMobileOrTablet && css`
            padding: 0;
            background: #fff;
        `}
    }
`;

const mobileSideBarBaseStyles = css`
    &&.ui-pane-sidebar {
        bottom: 0;
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
        z-index: 1002;

        .dark-mode & > .panel {
            background: #333;
        }
    }
`;

const mobileOverlayDrawerStyles = css`
    ${mobileSideBarBaseStyles}
    background: rgba(0, 0, 0, ${({ $show }) => $show ? 0.18 : 0});
    display: block;
    height: 100%;
    opacity: ${({ $show }) => $show ? 1 : 0};
    overflow: hidden;
    pointer-events: ${({ $show }) => $show ? "auto" : "none"};
    transition: background-color 200ms ease, opacity 200ms ease;

    > .panel {
        background: #fff;
        bottom: 0;
        box-shadow: 1px 0 10px rgba(0, 0, 0, .18);
        box-sizing: border-box;
        height: 100%;
        left: 0;
        overflow-x: hidden;
        overflow-y: hidden;
        padding: 8px 8px 50px 8px;
        position: absolute;
        top: 0;
        transform: translateX(${({ $show }) => $show ? "0" : "-100%"});
        transition: transform 240ms ease;
    }
`;

const mobileLegacySidebarStyles = css`
    ${mobileSideBarBaseStyles}
    display: ${({ $show }) => $show ? "block" : "none"};
    height: 100dvh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;

    > .panel {
        bottom: 0;
        box-shadow: 1px 0 10px rgba(0, 0, 0, .5);
        left: 0;
        overflow-x: hidden;
        overflow-y: auto;
        padding-bottom: 50px;
        position: absolute;
        top: 0;
    }
`;

const StyledUIPaneSideBar = styled.div`
    ${({ $isMobile, $mobileVariant }) => {
        if (!$isMobile) {
            return "";
        }

        if ($mobileVariant === "overlay") {
            return mobileOverlayDrawerStyles;
        }

        return mobileLegacySidebarStyles;
    }}
`;

export class UIPaneSideBar extends Component {
    constructor() {
        super();
        this.ref = React.createRef();
    }

    render() {
        const { show, mobileSideBar, className, onHide, onResize, style, children } = this.props;
        const mobileVariant = app.isMobileOrTablet && mobileSideBar ? "overlay" : "legacy";

        return (
            <StyledUIPaneSideBar
                ref={this.ref}
                className={`ui-pane-sidebar ${className ?? ""}`}
                $isMobile={app.isMobileOrTablet}
                $mobileVariant={mobileVariant}
                $show={!!show}
                onClick={e => {
                    e.stopPropagation();
                    onHide && onHide();
                }}
            >
                <StyledPanel
                    className="panel"
                    style={style}
                    onClick={e => e.stopPropagation()}
                >{children}</StyledPanel>

                {
                    !!onResize &&
                    <UIPaneSideBarResizer
                        sideBarRef={this.ref}
                        onResize={onResize}
                    />
                }
            </StyledUIPaneSideBar>
        );
    }
}

export class UIPaneSideBarResizer extends Component {
    handleMouseDown = event => {
        let { sideBarRef, onResize } = this.props;

        $("body").on("mousemove.drag", event => {
            onResize(event.pageX - sideBarRef.current.getBoundingClientRect().left);
        });
        $("body").on("mouseup.drag", event => {
            $("body").off(".drag");
        });
    }

    render() {
        return (
            <Resizer onMouseDown={this.handleMouseDown} />
        );
    }
}

/**
 * @typedef {object} UIPaneContentsProps
 * @property {string} [className] - Additional CSS class name(s) for the content container.
 * @property {React.ReactNode} [children] - The content to be rendered inside the pane contents.
 * @property {React.UIEventHandler<HTMLDivElement>} [onScroll] - Scroll event handler for the content container.
 * @property {React.CSSProperties} [style] - Custom CSS styles for the content container.
 * @property {React.Ref<HTMLDivElement>} [containerRef] - Ref to the content container div element.
 */

/**
 * A component used to wrap content within a UI pane.
 * @augments {React.Component<UIPaneContentsProps>}
 */
export class UIPaneContents extends Component {
    render() {
        const { className, children, onScroll, style, containerRef } = this.props;

        return (
            <div className={`ui-inner-pane ${className ?? ""}`} onScroll={onScroll} style={style} ref={containerRef}>
                {children}
            </div>
        );
    }
}

const StyledUINavigationBackButton = styled.div`
    width: 100%;
    
    background-color: ${props => props.setAsPlaceholder ? "none" : "#222"};
    cursor: ${props => props.setAsPlaceholder ? "auto" : "pointer"};

    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    min-height: 50px;

    img {
      height: 15px;
    }

    &:hover {
      background: $newBlue;
    }
`;

export class UINavigation extends Component {
    static contextType = UIContainerContext;

    render() {
        const { onBack, setAsPlaceholder = false } = this.props;

        let onSelect = event => {
            this.props.onSelect(event.currentTarget.id);
        };

        return (
            <div className={`ui-navigation ${this.props.className || ""} animate-in`}>
                <StyledUINavigationBackButton
                    id="ui-navigation-back-button"
                    setAsPlaceholder={setAsPlaceholder}
                    onClick={() => {
                        if (onBack) onBack();
                    }}
                >
                    {!setAsPlaceholder && <img src={getStaticUrl("/images/backIcon.svg")} />}
                </StyledUINavigationBackButton>
                <div className={"ui-navigation-items " + (this.props.fullHeight ? "full-height" : "")}>
                    {React.Children && React.Children.map(this.props.children, child => {
                        if (child) {
                            return React.cloneElement(child, {
                                key: child.props.id,
                                selected: this.props.selected,
                                onSelect
                            });
                        }
                    })}
                </div>
            </div>
        );
    }
}

export class UINavigationItemHeader extends Component {
    render() {
        const { children, fullHeight, title, selected, onSelect } = this.props;
        return (
            <div className={"ui-navigation-item-header " + (fullHeight ? "full-height" : "")}>
                {title && (
                    <span className="title">{title}</span>
                )}
                {React.Children.map(children, child => {
                    if (child) {
                        return React.cloneElement(child, {
                            selected,
                            onSelect
                        });
                    }
                })}
            </div>
        );
    }
}

export const UINavigationSectionLabel = styled.div`
  color: white;
  font-size: 15px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 6px 10px;
`;

export class UINavigationItem extends Component {
    render() {
        const { selected: selectedOption, id, onCustomSelect, onSelect, hidden, vertical, styles, icon, iconLabel, image, title, children, className } = this.props;
        const selected = selectedOption === id;
        // If there is a custom select on UINavigationItem use that otherwise use onSelect from UINavigation
        const handleClick = onCustomSelect ?? onSelect;

        if (hidden) return null;

        return (
            <div
                id={id}
                className={`${className ?? ""} ui-navigation-item ${selected ? "selected" : ""} ${vertical ? "vertical" : ""}`}
                onClick={handleClick}
                style={styles}
            >
                <div className={classNames("nav-label")}>
                    {icon && <div
                        className="icon-container"
                        style={
                            typeof icon === "string"
                                ? { marginRight: 10 }
                                : undefined
                        }
                    >
                        <Icon white size={24}>{icon}</Icon>
                        {iconLabel && <span className="icon-label">{iconLabel}</span>}
                    </div>}
                    {image && <div className="icon-container">{image}</div>}
                    {title}
                </div>
                {React.Children.map(children, child => {
                    if (child) {
                        return React.cloneElement(child, {
                            selected: selectedOption,
                            onSelect: onSelect
                        });
                    }
                })}
            </div>
        );
    }
}

/**
 * @typedef {object} SectionProps
 * @property {string} [id] - The HTML id attribute for the section element.
 * @property {React.ReactNode} [title] - The title content for the section.
 * @property {React.ReactNode} [description] - Additional description content for the section.
 * @property {React.ReactNode} [children] - The main content of the section.
 * @property {boolean} [disabled] - Whether the section is disabled.
 */

/**
 * A component used to structure content into distinct sections with optional title, badge, and description.
 * @augments {React.Component<SectionProps>}
 */

export class Section extends Component {
    render() {
        return (
            <section id={this.props.id} className={this.props.disabled ? "section-disabled" : ""}>
                {
                    this.props.title &&
                    <div className="section-title">{this.props.title}</div>
                }
                {!this.props.title && <Gap5 />}
                {
                    this.props.description &&
                    <div className="section-description">{this.props.description}</div>
                }

                {this.props.children}
            </section>
        );
    }
}

export class Field extends Component {
    render() {
        return (
            <section className="field">
                {this.props.title && <div className="field-label">{this.props.title}</div>}
                {this.props.children}
            </section>
        );
    }
}

/**
 * @typedef {import("@material-ui/core").ButtonProps} MuiButtonPropsAlias
 */

/**
 * @typedef {object} BlueButtonProps
 * // Inherits all props from Material-UI's ButtonProps
 * @augments {MuiButtonPropsAlias}
 * @property {React.ReactNode} [children] - The content of the button.
 */

/**
 * A blue-themed Material-UI Button component.
 * Renders a Button with variant="contained" and color="primary" by default,
 * accepting all standard Material-UI Button props.
 * @augments {React.Component<BlueButtonProps>}
 */
export class BlueButton extends Component {
    render() {
        return (
            <Button variant="contained" color="primary" {...this.props}>
                {this.props.children}
            </Button>
        );
    }
}

export class WarningButton extends Component {
    render() {
        return (
            <Button variant="contained" color="primary" {...this.props} className="warning">
                {this.props.children}
            </Button>
        );
    }
}

export class GreenButton extends Component {
    render() {
        const authoringStyle = {
            backgroundColor: themeColors.authoringThemeBaseColor,
            color: "white"
        };
        return (
            <Button variant="contained" style={authoringStyle} {...this.props}>
                {this.props.children}
            </Button>
        );
    }
}

export class BlueOutlinedButton extends Component {
    render() {
        return (
            <Button variant="outlined" color="primary" {...this.props}>
                {this.props.children}
            </Button>
        );
    }
}

export class YellowButton extends Component {
    render() {
        return (
            <Button variant="contained" {...this.props} className="yellow">
                {this.props.children}
            </Button>
        );
    }
}

export class ScaryButton extends Component {
    render() {
        return (
            <Button variant="contained" {...this.props} className={`orange ${this.props.className ?? ""}`}>
                {this.props.children}
            </Button>
        );
    }
}

export class DarkButton extends Component {
    render() {
        return (
            <Button variant="contained" {...this.props} className="dark">
                {this.props.children}
            </Button>
        );
    }
}

export class TextButton extends Component {
    render() {
        return (
            <Button variant="text" {...this.props} className="transparent">
                {this.props.children}
            </Button>
        );
    }
}

export const WhiteIconButton = styled(IconButton)`
    &&& {
    .MuiIcon-root {
        color: white;
        transition: color 300ms;
    }

    &:hover {
        .MuiIcon-root {
        color: ${themeColors.ui_blue};
        }
    }
    }
`;

export const RoundIconButton = styled(IconButton)`
  .MuiIcon-root {
    color: white;
    font-size: 20px;
  }

  &&& {
    background: ${themeColors.ui_blue};
    padding: 4px;
  }
`;

export const WhiteTextButton = styled(Button)`
    &&& {
        color: white;
  .MuiIcon-root {
    color: white;
  }

  .MuiButton-label {
    color: white;
  }
}`;

const StyledIconButton = styled(IconButton)`
   &&& {

        > span {
            ${({ $menuIsOpen }) => $menuIsOpen ? "opacity:1" : ""};
        }


        &:hover {
            background-color: transparent;

            span > span.MuiIcon-root{
                color: ${({ $colorOnHover }) => $colorOnHover ?? "inherit"};
            }
        }
    }
`;

const StyledMenu = styled(Menu)`
    &&& {
        .MuiPaper-root{
            border-radius: 2px;
            background: #FFF;
            box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.20);
            padding: 5px 0;
            overflow: hidden;
        }
    }
`;

// https://stackoverflow.com/questions/49834251/how-to-extend-styled-component-without-passing-props-to-underlying-dom-element
// Adding $ to the props that throws the following error:
// React does not recognize the `{prop name}` prop on a DOM element. If you intentionally want it to appear in the DOM as a custom attribute, spell
// it as lowercase `{prop name}` instead. If you accidentally passed it from a parent component, remove it from the DOM element.
export class IconMenu extends Component {
    state = {
        anchorEl: null
    }

    constructor(props) {
        super(props);
    }

    setAncholElState = state => this.setState({ anchorEl: state });

    handleShowMenu = event => {
        event.stopPropagation();
        this.setAncholElState(event.currentTarget);
        this.props.onShowMenu?.();
    }

    handleCloseMenu = () => {
        this.setAncholElState(null);
        this.props.handleCloseMenu?.();
    }

    handleMouseEvent = event => {
        event.stopPropagation();
    }

    handleMenuItemClick = (callback, event) => {
        if (callback) {
            event.stopPropagation();
            this.handleCloseMenu();
            callback(event);
        }
    }

    render() {
        const {
            icon = "more_vert",
            ariaLabel, size = "medium",
            iconButtonColor = "default",
            fontSize = "small",
            disabled,
            children,
            classes,
            buttonLabel,
            handleCloseMenu,
            transformOrigin = {
                vertical: "top",
                horizontal: "center"
            },
            anchorOrigin = {
                vertical: "bottom",
                horizontal: "center"
            },
            disableAutoFocusItem = false,
            ...rest
        } = this.props;
        const { anchorEl } = this.state;

        const isMenuOpen = !!anchorEl;
        const iconColor = iconButtonColor === "default" ? "inherit" : iconButtonColor;

        return (
            <Fragment>
                <StyledIconButton
                    classes={classes}
                    aria-haspopup="true"
                    aria-label={ariaLabel}
                    onClick={this.handleShowMenu}
                    onMouseDown={this.handleMouseEvent}
                    onMouseUp={this.handleMouseEvent}
                    onDoubleClick={this.handleMouseEvent}
                    color={iconButtonColor}
                    disabled={disabled}
                    size={size}
                    {...rest}
                >
                    <Icon fontSize={fontSize} color={iconColor}>{icon}</Icon>
                    {buttonLabel && <label>{buttonLabel}</label>}
                </StyledIconButton>
                <StyledMenu
                    variant="menu"
                    anchorEl={this.state.anchorEl}
                    getContentAnchorEl={null}
                    open={isMenuOpen}
                    onClose={this.handleCloseMenu}
                    onClick={this.handleCloseMenu}
                    anchorOrigin={anchorOrigin}
                    transformOrigin={transformOrigin}
                    disableAutoFocusItem
                    disableRestoreFocus
                >
                    {React.Children.map(children, child =>
                        child ? React.cloneElement(child, {
                            onClick: event => this.handleMenuItemClick(child.props.onClick, event),
                            onMouseDown: this.handleMouseEvent,
                            onMouseUp: this.handleMouseEvent,
                            onDoubleClick: this.handleMouseEvent,
                        }) : null
                    )}
                </StyledMenu>
            </Fragment>
        );
    }
}

export class ContextMenu extends Component {
    handleCloseMenu = () => {
        this.props.onClose && this.props.onClose();
    }

    handleMenuItemClick = (callback, event) => {
        event.stopPropagation();

        if (!callback) {
            return;
        }

        this.handleCloseMenu();
        callback(event);
    }

    render() {
        const { x, y, children } = this.props;
        const handleMouseEvent = event => {
            event.stopPropagation();
        };

        return (
            <Popover
                open={true}
                anchorReference="anchorPosition"
                anchorPosition={{ top: y, left: x }}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                onClose={this.handleCloseMenu}
            >
                <MenuList
                    variant="menu"
                    onMouseDown={handleMouseEvent}
                    onMouseUp={handleMouseEvent}
                    onClick={handleMouseEvent}
                    onDoubleClick={handleMouseEvent}
                >
                    {React.Children.map(children, child =>
                        child ? React.cloneElement(child, {
                            onClick: event => this.handleMenuItemClick(child.props.onClick, event),
                            onMouseDown: handleMouseEvent,
                            onMouseUp: handleMouseEvent,
                            onDoubleClick: handleMouseEvent,
                        }) : null
                    )}
                </MenuList>
            </Popover>
        );
    }
}

/**
 * @typedef {Object} DividerProps
 * @property {boolean} [hidden]
 * @property {string} [color]
 * @property {number} [margin]
 */

/**
 * @type {import('styled-components').StyledComponent<'hr', any, DividerProps, never>}
 */
export const Divider = styled.hr`
    // Adding &&& to override the material ui style
    &&& {
        display: ${props => props.hidden ? "none" : "block"};
        border: none;
        height: 1px;
        background: ${props => props.color ?? "#eee"};
        width: 100%;
        margin-top: ${props => props.margin != null ? props.margin : 30}px;
        margin-bottom: ${props => props.margin != null ? props.margin : 30}px;
    }
`;
